import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@vnetwork/ui/feedback';
import { useAuth } from '@/auth/AuthProvider';
import { useMyServices } from '@/api/services';
import { useProviders, useActivateService } from '@/api/service-provisioning';
import { useServiceWafStrategies, waapAsArray, type WafStrategyRow } from '@/api/waap/strategies';
import { useWorkspace } from '@/api/tiger';
import { trackServiceActivate } from '@/lib/analytics-events';
import {
  WAAP_PROVIDER,
  WAAP_PRODUCT,
  waapConsoleRoute,
} from '@/pages/services/waap/waap-service';
import { isWafCapable } from '@/pages/services/waf/waf-service';
import { useServiceWaapDomainsPaged } from '@/api/manticore/service-waap';
import { WaapOnboardingHero } from './WaapOnboardingHero';

/**
 * In-app WAAP promo upsell — the eligibility + dismiss gating around the shared
 * <WafPromoBanner>.
 *
 * Audience: shown to ALL customers as a security upsell, EXCEPT those who already
 * run a WAAP (`cobra`) or WAF (Owl/Mlytics) service — nothing to upsell once they
 * have edge security. Self-manages dismissal (sessionStorage).
 *
 * The CTA provisions in one tap: it fires the same activation as the WAAP landing
 * (create the standalone `cobra` service), then drops the customer into the
 * quick-setup wizard — no intermediate hero page. If a WAAP service already exists
 * (e.g. still provisioning), it skips activation and resumes that service's wizard.
 *
 * Renders null while services load, once dismissed (per browser session), or when
 * the customer already has an active WAAP/WAF service. (The sign-up page renders
 * <WafPromoBanner> directly — no auth-scoped gating there.)
 */
export function WafFreePromo() {
  const navigate = useNavigate();
  const { show } = useToast();
  const { user } = useAuth();
  const { data: services = [], isLoading } = useMyServices();
  const { data: providers = [] } = useProviders();
  const activate = useActivateService();

  // Any WAAP (cobra) service (even mid-provisioning) → resume its wizard instead
  // of provisioning a duplicate; also the anchor for the domain-count lookup.
  const existingWaap = useMemo(
    () => services.find((s) => s.provider?.name?.trim().toLowerCase() === WAAP_PROVIDER),
    [services],
  );

  // Does the WAAP service actually protect a domain yet? A freshly-activated
  // cobra service is `active` immediately and always carries one service_zone
  // (the vendor tenant), so zones_count can't tell "onboarded" from "just
  // activated" — only a registered WAAP domain (domain_services type='waap')
  // can. Fetch its domain count; the query self-disables until a WAAP service
  // exists (serviceId ''), so accounts without WAAP make no extra call.
  const { data: waapDomains, isFetched: waapDomainsFetched } = useServiceWaapDomainsPaged(
    existingWaap?.id ?? '',
    { page: 1, perPage: 1 },
  );
  const waapDomainCount = waapDomains?.pagination.total ?? 0;

  // AI-WAF (General Protection) strategies for the WAAP service — one row per
  // protected website carrying the applied managed-policy id (`strategy_type`)
  // and its `enable` flag. Self-disables until a WAAP service exists (serviceId '').
  // Every WAAP domain is auto-provisioned with the default Basic policy
  // (strategy_type 1) already enabled, so "Basic + enabled" does NOT mean the
  // customer configured AI-WAF. Count the final step done only once a stronger
  // managed policy is applied (strategy_type > 1) — i.e. they actually tuned it.
  const { data: waapStrategies, isFetched: waapStrategiesFetched } =
    useServiceWafStrategies(existingWaap?.id ?? '');
  const aiWafConfigured = waapAsArray<WafStrategyRow>(waapStrategies).some(
    (r) => r.enable !== false && Number(r.strategy_type) > 1,
  );

  // A customer already running the Owl/Mlytics WAF product has edge security —
  // nothing to upsell — so keep hiding the banner for them (audience gating).
  const hasActiveWaf = services.some(
    (s) => isWafCapable(s) && s.status?.toLowerCase() === 'active',
  );

  // Step 3 (CertInvent): the org has provisioned its certificate workspace.
  // CertInvent (codename tiger) isn't a normal provisioned service — its "active"
  // state is a `GET /workspace` that returns null until activated.
  const { data: certWorkspace, isFetched: workspaceFetched } = useWorkspace();
  const hasActiveCertinvent = certWorkspace != null;

  // WAAP is provisioned on its own `cobra` provider; only an active provider can
  // be provisioned self-serve.
  const cobra = useMemo(
    () =>
      providers.find(
        (p) => p.name?.trim().toLowerCase() === WAAP_PROVIDER && p.status === 'active',
      ),
    [providers],
  );

  // ── Stepper state (real signals, no faking) ────────────────────────────────
  // Step 1: org exists (user has an organizationId or any service has one).
  const orgId = user?.organizationId || services[0]?.organization_id || '';
  const step1Done = Boolean(orgId);

  // Step 2 (Domain & origin): a WAAP domain is registered (waapDomainCount > 0).
  // Step 3 (CertInvent): an SSL-certificate service is active.
  // Step 4 (AI-WAF rules): a managed AI-WAF policy is enabled on a website — the
  // banner is gated off once this completes. Don't infer from a CDN domain.
  const step2Done = waapDomainCount > 0;
  const step3Done = hasActiveCertinvent;
  const step4Done = aiWafConfigured;

  const heroSteps = [
    { label: 'Create organization', meta: orgId ? `ID ${orgId}` : '', done: step1Done },
    { label: 'Domain & origin',     meta: '~2 min',                    done: step2Done },
    { label: 'CertInvent',          meta: 'Activate SSL certificates',  done: step3Done },
    { label: 'AI-WAF rules',        meta: 'Enable a protection policy', done: step4Done },
  ];

  function handleActivate() {
    if (activate.isPending) return;
    // Already have a WAAP service → resume at the first INCOMPLETE step instead
    // of restarting the quick-setup wizard (which re-processes an already-set-up
    // account). This keeps "Continue setup" pointing at what's actually left.
    if (existingWaap) {
      const base = waapConsoleRoute(existingWaap.id);
      if (!step2Done) navigate(`${base}/onboarding`);          // add a protected domain
      else if (!step3Done) navigate('/services/certinvent');   // activate managed SSL
      else navigate(`${base}/aiwaf/general`);                  // configure AI-WAF rules
      return;
    }
    const email = user?.email ?? '';
    if (!cobra || !email) {
      // Provider not enabled for self-serve on this account, or no email — fall
      // back to the activation landing, which explains the unavailable state.
      navigate('/services/waap');
      return;
    }
    activate.mutate(
      { serviceName: WAAP_PRODUCT.serviceName, providerId: cobra.id, email },
      {
        onSuccess: (service) => {
          trackServiceActivate(WAAP_PROVIDER, 'waap');
          show({ tone: 'success', title: 'WAAP activated' });
          navigate(`${waapConsoleRoute(service.id)}/onboarding`);
        },
        onError: (err) =>
          show({
            tone: 'danger',
            title: 'Activation failed',
            description: err.message || 'Could not activate WAAP. Please try again.',
          }),
      },
    );
  }

  // Reveal only once every signal has SETTLED (fetched) — gate on data-fetched,
  // not "!isLoading". A query in its enable→fetch transition briefly reports
  // isLoading=false, which flashed the banner on then off ("hiện rồi mất"). A
  // half-loaded state also reads steps as incomplete and mis-routes "Continue
  // setup" back into quick-setup. Disabled queries (no WAAP service) never fetch,
  // so guard them behind existingWaap; the always-on workspace query gates every
  // account. Then hide once the final step (AI-WAF rules) is done, or for
  // customers already on the standalone Owl/Mlytics WAF product.
  const waapExtrasReady = !existingWaap || (waapDomainsFetched && waapStrategiesFetched);
  const settled = !isLoading && workspaceFetched && waapExtrasReady;
  if (!settled || hasActiveWaf || step4Done) return null;

  return (
    <WaapOnboardingHero
      steps={heroSteps}
      onActivate={handleActivate}
      activating={activate.isPending}
      // Once the WAAP service exists, the CTA resumes the setup wizard rather
      // than activating — label it accordingly.
      ctaLabel={existingWaap ? 'Continue setup' : undefined}
    />
  );
}
